/**
 * Reply-To address policy (#433).
 *
 * The advisor picks the address clients reply to. Two kinds are honoured on
 * outgoing mail:
 *   - platform domain (the domain of the SES sender): replies are received by
 *     SES and land in the in-app Inbox; usable immediately.
 *   - the advisor's own firm address: Amazon SES must confirm it first
 *     (UserProfile.replyEmailVerifiedAt); replies then go to that mailbox and
 *     are not captured in-app.
 * Anything else is never placed on an email. The email-sender Lambda applies
 * the same rule server-side (`_resolve_reply_to`), so this module is the UX
 * layer, not the security boundary.
 */

export const DEFAULT_PLATFORM_REPLY_DOMAIN = 'finnewsy.com';

export const REPLY_EMAIL_STATUS = Object.freeze({
  NONE: 'none',
  PLATFORM: 'platform',
  VERIFIED: 'verified',
  PENDING: 'pending',
});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Domain of the SES sender (VITE_SES_FROM_EMAIL), lowercased. */
export function getPlatformReplyDomain() {
  const sender = String(import.meta.env?.VITE_SES_FROM_EMAIL || '').trim().toLowerCase();
  const at = sender.lastIndexOf('@');
  return at > 0 && at < sender.length - 1 ? sender.slice(at + 1) : DEFAULT_PLATFORM_REPLY_DOMAIN;
}

export function normalizeReplyEmail(email) {
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

/** @returns {string|null} a user-facing problem, or null when the address is well-formed */
export function validateReplyEmail(email) {
  const value = normalizeReplyEmail(email);
  if (!value) return 'Enter the email address clients should reply to';
  if (!EMAIL_PATTERN.test(value)) return 'Enter a valid email address, like you@yourfirm.com';
  return null;
}

export function isPlatformReplyAddress(email) {
  const value = normalizeReplyEmail(email);
  const at = value.lastIndexOf('@');
  return at > 0 && value.slice(at + 1) === getPlatformReplyDomain();
}

/** @returns {'none'|'platform'|'verified'|'pending'} */
export function getReplyEmailStatus(profile) {
  const value = normalizeReplyEmail(profile?.replyEmail);
  if (!value) return REPLY_EMAIL_STATUS.NONE;
  if (isPlatformReplyAddress(value)) return REPLY_EMAIL_STATUS.PLATFORM;
  return profile?.replyEmailVerifiedAt ? REPLY_EMAIL_STATUS.VERIFIED : REPLY_EMAIL_STATUS.PENDING;
}

export function isReplyEmailUsable(profile) {
  const status = getReplyEmailStatus(profile);
  return status === REPLY_EMAIL_STATUS.PLATFORM || status === REPLY_EMAIL_STATUS.VERIFIED;
}

/**
 * The Reply-To an email may carry: a usable override (Step-4 field) first,
 * else the profile's usable address, else null.
 */
export function resolveReplyTo({ override, profile } = {}) {
  const usable = (address) => {
    const value = normalizeReplyEmail(address);
    if (!value) return null;
    if (isPlatformReplyAddress(value)) return value;
    if (profile && value === normalizeReplyEmail(profile.replyEmail) && profile.replyEmailVerifiedAt) return value;
    return null;
  };
  return usable(override) ?? usable(profile?.replyEmail) ?? null;
}
